
import React from 'react';
import { Property } from '../../../../types';
import { SpinnerIcon, SaveIcon, CheckCircleIcon, FireIcon } from '../../../../components/Icons';
import { StatusChip } from './StatusChip';
import { SCHEDULE_REGEX } from './OperationConstants';

interface OperationTableRowProps {
    property: Property;
    allowedOwners: string[];
    localEdits: Partial<Property>;
    isSaving: boolean;
    onLocalEdit: (field: keyof Property, value: any) => void;
    onSave: () => void;
    onOpenScheduleEditor?: (id: string, initialValue: string) => void;
}

export const OperationTableRow: React.FC<OperationTableRowProps> = ({
    property, allowedOwners, localEdits, isSaving, onLocalEdit, onSave, onOpenScheduleEditor
}) => {
    const isDirty = Object.keys(localEdits).length > 0;
    const getVal = (f: keyof Property) => {
        if (localEdits[f] !== undefined) return localEdits[f];
        if (property[f] !== undefined && property[f] !== null && property[f] !== '') return property[f];
        if (f === 'nombre_cliente') return (property as any).nombre_contacto || '';
        if (f === 'telefono_cliente') return (property as any).celular_contacto || (property as any).telefono_contacto || '';
        if (f === 'correo_1') return (property as any).correo_contacto || (property as any).email || '';
        if (f === 'codigo_brp') return (property as any).codigo_brp_o_inmobiliaria || '';
        return '';
    };
    const currentStatus = getVal('status') as string;
    const isActualizado = currentStatus === 'Actualizado';
    const isPreventa = !!(localEdits.es_preventa !== undefined ? localEdits.es_preventa : property.es_preventa);

    const renderCell = (field: keyof Property, placeholder: string = "---", isDisabled: boolean = false) => {
        const val = getVal(field) as string;
        const isInvalid = field === 'disponibilidad_visitas' && val && !SCHEDULE_REGEX.test(val);
        
        if (field === 'disponibilidad_visitas') {
            return (
                <input 
                    type="text"
                    value={val}
                    placeholder={placeholder}
                    disabled={isDisabled}
                    onClick={() => !isDisabled && onOpenScheduleEditor && onOpenScheduleEditor(property.id, val)}
                    readOnly
                    className={`w-full px-2 py-1.5 text-xs border-2 rounded-lg transition-all outline-none bg-transparent cursor-pointer hover:bg-slate-50 ${isDisabled ? 'opacity-30 cursor-not-allowed' : (isInvalid ? 'border-red-500 bg-red-50' : (isDirty && localEdits[field] !== undefined ? 'border-amber-200 bg-amber-50/30' : 'border-transparent hover:border-gray-200 focus:border-sky-500 focus:bg-white'))}`}
                />
            );
        }

        return (
            <input 
                type="text"
                value={val}
                onChange={(e) => onLocalEdit(field, e.target.value)}
                placeholder={placeholder}
                disabled={isDisabled}
                className={`w-full px-2 py-1.5 text-xs border-2 rounded-lg transition-all outline-none bg-transparent ${isDisabled ? 'opacity-30 cursor-not-allowed' : (isInvalid ? 'border-red-500 bg-red-50' : (isDirty && localEdits[field] !== undefined ? 'border-amber-200 bg-amber-50/30' : 'border-transparent hover:border-gray-200 focus:border-sky-500 focus:bg-white'))}`}
            />
        );
    };

    return (
        <tr className={`hover:bg-sky-50/30 transition-colors ${isDirty ? 'bg-amber-50/10' : ''}`}>
            <td className="px-4 py-3 text-xs font-bold text-sky-900 sticky left-0 bg-white border-r border-gray-100 z-10">
                {isActualizado && property.codigo_actual ? (
                    <div className="flex flex-col">
                        <span className="line-through text-gray-400 text-[10px]">{property.codigo}</span>
                        <span className="text-blue-600 font-black">{property.codigo_actual}</span>
                    </div>
                ) : <span>{property.codigo}</span>}
            </td>
            <td className="px-4 py-3">
                <div className="flex flex-col items-center gap-1">
                    <StatusChip status={currentStatus} onSelect={(s) => onLocalEdit('status', s)} />
                    <button 
                        onClick={() => onLocalEdit('es_preventa', !isPreventa)}
                        className={`p-1 rounded-md transition-all flex items-center gap-1 ${isPreventa ? 'bg-indigo-600 text-white shadow-md' : 'bg-gray-100 text-gray-400'}`}
                        title={isPreventa ? "Inmueble en Preventa" : "Marcar como Preventa"}
                    >
                        <FireIcon className={`w-3.5 h-3.5 ${isPreventa ? 'animate-pulse' : ''}`} />
                        <span className="text-[8px] font-black">{isPreventa ? 'PRE' : ''}</span>
                    </button>
                </div>
            </td>
            <td className="px-4 py-3">{renderCell('codigo_actual', 'Ej: 183150-XXXX', !isActualizado)}</td>
            <td className="px-4 py-3">
                <select 
                    value={getVal('owner') as string}
                    onChange={(e) => onLocalEdit('owner', e.target.value)}
                    className={`w-full text-xs font-bold border-2 rounded-lg px-2 py-1.5 cursor-pointer bg-transparent outline-none ${isDirty && localEdits.owner ? 'border-amber-200 bg-amber-50' : 'border-transparent hover:border-gray-200 focus:border-sky-500 focus:bg-white'}`}
                >
                    <option value="">Seleccionar...</option>
                    {allowedOwners.map(o => <option key={o} value={o}>{o}</option>)}
                    {getVal('owner') && !allowedOwners.includes(getVal('owner') as string) && (
                        <option value={getVal('owner') as string}>{getVal('owner') as string}</option>
                    )}
                </select>
            </td>
            <td className="px-4 py-3">{renderCell('codigo_brp')}</td>
            <td className="px-4 py-3">{renderCell('numero_matricula')}</td>
            <td className="px-4 py-3">{renderCell('numero_predial')}</td>
            <td className="px-4 py-3">{renderCell('piso')}</td>
            <td className="px-4 py-3">{renderCell('valor_administracion', '$ 0')}</td>
            <td className="px-4 py-3">{renderCell('disponibilidad_visitas', 'Lu-Vi 08:00-17:00; Sá 09:00-12:00')}</td>
            <td className="px-4 py-3">{renderCell('nombre_cliente')}</td>
            <td className="px-4 py-3">{renderCell('telefono_cliente')}</td>
            <td className="px-4 py-3">{renderCell('correo_1')}</td>
            <td className="px-4 py-3">{renderCell('nombre_encargado')}</td>
            <td className="px-4 py-3">{renderCell('telefono_encargado')}</td>
            <td className="px-4 py-3">{renderCell('observaciones_internas', 'Sin observaciones')}</td>
            <td className="px-4 py-3 text-center sticky right-0 bg-white z-10 border-l border-gray-100 shadow-[-4px_0_10px_-4px_rgba(0,0,0,0.05)]">
                {isDirty ? (
                    <button onClick={onSave} disabled={isSaving} className="p-2 bg-sky-600 text-white rounded-lg shadow hover:bg-sky-700 active:scale-90 transition-all disabled:opacity-50">
                        {isSaving ? <SpinnerIcon className="w-3 h-3 animate-spin" /> : <SaveIcon className="w-3 h-3" />}
                    </button>
                ) : (
                    <CheckCircleIcon className="w-5 h-5 text-gray-200 mx-auto" />
                )}
            </td>
        </tr>
    );
};
